import { getHexTileMapKey } from '../hexmap/model/keys';
import type { RuleSet } from '../rules/schema';
import { holdsStock, matchDuration, type PlayerId, type SimState, territoryOwners } from '../sim';
import { cityName } from './cities';
import { clockSeconds } from './matchClock';
import { type UpgradeProgress, upgradeProgress } from './upgradeProgress';

/** One of the viewer's own upgrades under way (the HUD's "Upgrading" list). */
export interface UpgradeInProgress extends UpgradeProgress {
  buildingId: string;
  /** The building's name ("Barracks", "City 1"). */
  name: string;
  /** "City 1 → Stronghold", "Barracks · City 1 → Level 2". */
  title: string;
  /** Whole seconds the match clock (`matchClock.ts`, as the HUD timer) reads when it finishes; null while paused. */
  finishClock: number | null;
  /** It would finish after a timed match ends (no finish time to show). */
  afterEnd: boolean;
}

/**
 * Every upgrade `playerId` has running, across their cities, soonest first,
 * the paused ones (no finish in sight) after the running ones, then by building id. Owner only: `upgradeProgress` skips a rival's, so
 * nothing about one leaks (`Building.upgrading`, docs/mechanics/fog.md).
 */
export function upgradesInProgress(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number> = new Map()
): UpgradeInProgress[] {
  const duration = matchDuration(state, rules);
  const rows: UpgradeInProgress[] = [];
  let owners: ReturnType<typeof territoryOwners> | undefined;
  for (const b of Object.values(state.buildings)) {
    const p = upgradeProgress(state, rules, b, playerId);
    if (!p) continue;
    const name = rules.buildings[b.type]?.name ?? b.type;
    const isCity = holdsStock(rules, b);
    owners ??= territoryOwners(state, rules);
    const city = isCity ? b : state.buildings[owners.get(getHexTileMapKey(b.pos)) ?? ''];
    const where = city ? cityName(city, playerId, cityNumbers) : '';
    rows.push({
      ...p,
      buildingId: b.id,
      name: isCity ? where || name : name,
      title: isCity
        ? `${where || name} → ${p.title}`
        : `${name}${where ? ` · ${where}` : ''} → ${p.title === name ? `Level ${p.level}` : p.title}`,
      finishClock: p.paused ? null : clockSeconds(state, rules, state.elapsed + p.secondsLeft),
      afterEnd: duration !== null && state.elapsed + p.secondsLeft >= duration,
    });
  }
  return rows.sort(
    (a, c) =>
      Number(a.paused) - Number(c.paused) ||
      a.secondsLeft - c.secondsLeft ||
      (a.buildingId < c.buildingId ? -1 : 1)
  );
}
